import type { FieldSelection, GlobalFieldId, GlobalPrimitiveFieldId, GlobalSingleRelationFieldId, IdFieldSelection, IdRelationQuery, RelationQuery } from '#layers/data-model';
import type { ResolvedMonospacePrimitiveField, ResolvedMonospaceSingleRelationField } from '#layers/data-model/types/schema/resolved-schema';
import { isFileThumbnailField } from '~/utils/fields/single-relation-field';

export interface IdToFieldSelectionContext {
	findPrimitiveFieldById: (id: GlobalPrimitiveFieldId) => ResolvedMonospacePrimitiveField | undefined;
	findSingleRelationFieldById: (id: GlobalSingleRelationFieldId) => ResolvedMonospaceSingleRelationField | undefined;
}

function translateFieldId(id: GlobalFieldId, ctx: IdToFieldSelectionContext): string | undefined {
	const primitive = ctx.findPrimitiveFieldById(id as GlobalPrimitiveFieldId);
	if (primitive) return primitive.apiName;

	const relation = ctx.findSingleRelationFieldById(id as GlobalSingleRelationFieldId);
	if (relation) return relation.apiName;

	return undefined;
}

function translateIdRelationQuery(query: IdRelationQuery, ctx: IdToFieldSelectionContext): RelationQuery {
	const result: RelationQuery = {
		fields: query.fields ? idFieldSelectionToFieldSelection(query.fields, ctx) : ['*'],
	};

	if (query.sort) {
		result.sort = query.sort.flatMap((id) => {
			const apiName = translateFieldId(id, ctx);
			return apiName ? [apiName] : [];
		});
	}

	if (query.limit !== undefined) result.limit = query.limit;
	if (query.offset !== undefined) result.offset = query.offset;
	if (query.filter !== undefined) result.filter = query.filter;

	return result;
}

export function idFieldSelectionToFieldSelection(
	selection: IdFieldSelection,
	ctx: IdToFieldSelectionContext,
): FieldSelection {
	const result: FieldSelection = [];

	for (const entry of selection) {
		if (typeof entry === 'string') {
			if (entry === '*') {
				result.push('*');
				continue;
			}

			const field = ctx.findPrimitiveFieldById(entry as GlobalPrimitiveFieldId);

			if (field) {
				result.push(field.apiName);
			}
			else if (import.meta.dev) {
				console.warn(`[idFieldSelectionToFieldSelection] Unexpected bare field ID "${entry}" — not a known primitive. Bare relation IDs should be expanded before reaching the translation layer.`);
			}
		}
		else {
			for (const [relationFieldId, nestedValue] of Object.entries(entry)) {
				const relationField = ctx.findSingleRelationFieldById(relationFieldId as GlobalSingleRelationFieldId);
				if (!relationField) continue;

				// $thumbnail is a client-side identity virtual relation ((item) => item)
				// that does not exist on the backend. Hoist its selection onto the parent
				// so the file fields its display needs are fetched, instead of nesting them
				// under a segment the remote query strips (leaving the thumbnail under-fetched).
				if (isFileThumbnailField(relationField) && Array.isArray(nestedValue)) {
					result.push(...idFieldSelectionToFieldSelection(nestedValue as IdFieldSelection, ctx));
					continue;
				}

				if (Array.isArray(nestedValue)) {
					result.push({
						[relationField.apiName]: idFieldSelectionToFieldSelection(nestedValue as IdFieldSelection, ctx),
					});
				}
				else {
					result.push({
						[relationField.apiName]: translateIdRelationQuery(nestedValue as IdRelationQuery, ctx),
					});
				}
			}
		}
	}

	return result;
}
